"use client"

import * as React from "react"
import { AppIcon } from "@/components/desk/AppIcon"
import { InstalledApp, OpenAppTab, App } from "@/types/workspace"
import { Settings, Rocket, X, MoreHorizontal, Sliders, EyeOff, Puzzle } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"

interface MacDockProps {
  activeAppId: string | null
  isDockVisible: boolean
  setIsDockVisible: (visible: boolean) => void
  dockPosition: "bottom" | "top" | "left" | "right"
  dockOrientation: "horizontal" | "vertical"
  dockAlignment: "left" | "center" | "right"
  dockNoMargin: boolean
  dockSize: number
  setDockSize: (size: number) => void
  dockAppIds: string[]
  setDockAppIds: React.Dispatch<React.SetStateAction<string[]>>
  dockWidgetIds: string[]
  installedApps: InstalledApp[]
  widgetsConfig: any[]
  openApps: OpenAppTab[]
  activeFloatingWidgets: string[]
  lang: string
  handleDockAppClick: (app: App) => void
  handleToggleFloatingWidget: (widgetId: string) => void
  handleOpenMarketplaceTab: () => void
  handleOpenSettingsTab: () => void
  handleOpenProfileTab?: () => void
  setProfileTab?: (tab: any) => void
  setSettingsTab: (tab: any) => void
  setActiveAppId: (id: string | null) => void
  setSplitActiveAppId: (id: string | null) => void
  handleDockDragStart: (e: React.MouseEvent) => void
  hiddenAppIds?: string[]
  setHiddenAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  setShowAddAppModal?: (show: boolean) => void
  hasCompany?: boolean
}

export function MacDock({
  activeAppId,
  isDockVisible,
  setIsDockVisible,
  dockPosition,
  dockOrientation,
  dockAlignment,
  dockNoMargin,
  dockSize,
  setDockSize,
  dockAppIds,
  setDockAppIds,
  dockWidgetIds,
  installedApps,
  widgetsConfig,
  openApps,
  activeFloatingWidgets,
  lang,
  handleDockAppClick,
  handleToggleFloatingWidget,
  handleOpenMarketplaceTab,
  handleOpenSettingsTab,
  handleOpenProfileTab,
  setProfileTab,
  setSettingsTab,
  setActiveAppId,
  setSplitActiveAppId,
  handleDockDragStart,
  hiddenAppIds = [],
  setHiddenAppIds,
  setShowAddAppModal,
  hasCompany = true,
}: MacDockProps) {
  const [contextMenu, setContextMenu] = React.useState<{
    x: number
    y: number
    type: "app" | "dock"
    app?: App
  } | null>(null)

  const forceVisible = activeAppId === null
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const dockRef = React.useRef<HTMLDivElement | null>(null)
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null)
  const widgetsPopoverRef = React.useRef<HTMLDivElement | null>(null)
  const [isWidgetsOpen, setIsWidgetsOpen] = React.useState(false)
  const userJustClickedOutsideRef = React.useRef<number>(0)
  const isDockVisibleRef = React.useRef(isDockVisible)

  React.useEffect(() => {
    isDockVisibleRef.current = isDockVisible
    if (!isDockVisible && !forceVisible) {
      setIsOverflowOpen(false)
      setIsWidgetsOpen(false)
    }
  }, [isDockVisible, forceVisible])

  const handleMouseEnter = React.useCallback(() => {
    if (Date.now() - userJustClickedOutsideRef.current < 400) return
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (!forceVisible) {
      setIsDockVisible(true)
    }
  }, [forceVisible, setIsDockVisible])

  const handleMouseMove = React.useCallback(() => {
    if (Date.now() - userJustClickedOutsideRef.current < 400) return
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (!forceVisible && !isDockVisibleRef.current) {
      setIsDockVisible(true)
    }
  }, [forceVisible, setIsDockVisible])

  const handleMouseLeave = React.useCallback(() => {
    if (forceVisible) return
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsDockVisible(false)
      setIsOverflowOpen(false)
      setIsWidgetsOpen(false)
      hideTimeoutRef.current = null
    }, 1200)
  }, [forceVisible, setIsDockVisible])

  // Ocultação imediata ao clicar fora do Dock e do Popover (com refs síncronas e trava temporal de mouse move)
  React.useEffect(() => {
    const dismissDock = () => {
      userJustClickedOutsideRef.current = Date.now()
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
      setIsDockVisible(false)
      setIsOverflowOpen(false)
      setIsWidgetsOpen(false)
    }

    const handlePointerOrMouseDown = (e: Event) => {
      const targetNode = e.target as Node | null
      const isInsideDock = !!(dockRef.current && targetNode && dockRef.current.contains(targetNode))
      const isInsidePopover = !!(
        (popoverContentRef.current && targetNode && popoverContentRef.current.contains(targetNode)) ||
        (widgetsPopoverRef.current && targetNode && widgetsPopoverRef.current.contains(targetNode))
      )

      if (!isInsideDock && !isInsidePopover) {
        setIsOverflowOpen(false)
        setIsWidgetsOpen(false)
        if (!forceVisible && isDockVisibleRef.current) {
          dismissDock()
        }
      }
    }

    const handleWindowBlur = () => {
      setIsOverflowOpen(false)
      setIsWidgetsOpen(false)
      if (forceVisible || !isDockVisibleRef.current) return
      dismissDock()
    }

    window.addEventListener("pointerdown", handlePointerOrMouseDown, true)
    window.addEventListener("mousedown", handlePointerOrMouseDown, true)
    window.addEventListener("click", handlePointerOrMouseDown, true)
    window.addEventListener("blur", handleWindowBlur)

    return () => {
      window.removeEventListener("pointerdown", handlePointerOrMouseDown, true)
      window.removeEventListener("mousedown", handlePointerOrMouseDown, true)
      window.removeEventListener("click", handlePointerOrMouseDown, true)
      window.removeEventListener("blur", handleWindowBlur)
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [forceVisible, setIsDockVisible])
  const isVertical = dockOrientation === "vertical" || dockPosition === "left" || dockPosition === "right"

  let dockPosClass = "fixed bottom-16 left-1/2 -translate-x-1/2"
  if (dockPosition === "bottom") {
    if (dockAlignment === "left") dockPosClass = "fixed bottom-4 left-4"
    else if (dockAlignment === "right") dockPosClass = "fixed bottom-4 right-4"
    else dockPosClass = "fixed bottom-4 left-1/2 -translate-x-1/2"
  } else if (dockPosition === "top") {
    if (dockAlignment === "left") dockPosClass = "fixed top-4 left-4"
    else if (dockAlignment === "right") dockPosClass = "fixed top-4 right-4"
    else dockPosClass = "fixed top-4 left-1/2 -translate-x-1/2"
  } else if (dockPosition === "left") {
    if (dockAlignment === "left") dockPosClass = "fixed left-4 top-4"
    else if (dockAlignment === "right") dockPosClass = "fixed left-4 bottom-4"
    else dockPosClass = "fixed left-4 top-1/2 -translate-y-1/2"
  } else if (dockPosition === "right") {
    if (dockAlignment === "left") dockPosClass = "fixed right-4 top-4"
    else if (dockAlignment === "right") dockPosClass = "fixed right-4 bottom-4"
    else dockPosClass = "fixed right-4 top-1/2 -translate-y-1/2"
  }

  let dockAnimationClass = ""
  if (forceVisible || isDockVisible) {
    if (isVertical) {
      dockAnimationClass = "translate-x-0 opacity-100 scale-100"
    } else {
      dockAnimationClass = "translate-y-0 opacity-100 scale-100"
    }
  } else {
    if (dockPosition === "bottom") {
      dockAnimationClass = "translate-y-24 opacity-0 scale-90 pointer-events-none"
    } else if (dockPosition === "top") {
      dockAnimationClass = "-translate-y-24 opacity-0 scale-90 pointer-events-none"
    } else if (dockPosition === "left") {
      dockAnimationClass = "-translate-x-24 opacity-0 scale-90 pointer-events-none"
    } else if (dockPosition === "right") {
      dockAnimationClass = "translate-x-24 opacity-0 scale-90 pointer-events-none"
    }
  }

  let containerClass = ""
  if (isVertical) {
    containerClass = dockNoMargin
      ? "flex flex-col items-center p-1.5 rounded-2xl"
      : "flex flex-col items-center px-3 py-3.5 rounded-2xl gap-3"
  } else {
    containerClass = dockNoMargin
      ? "flex flex-row items-center p-1.5 rounded-2xl"
      : "flex flex-row items-center px-3.5 py-3 rounded-2xl gap-3"
  }

  let separatorClass = ""
  if (isVertical) {
    separatorClass = dockNoMargin
      ? "h-px w-full bg-white/10 dark:bg-zinc-800/30 my-1.5 shrink-0"
      : "h-px w-8 bg-white/20 dark:bg-zinc-800/50 self-center my-1 shrink-0"
  } else {
    separatorClass = dockNoMargin
      ? "w-px h-full bg-white/10 dark:bg-zinc-800/30 mx-1.5 shrink-0"
      : "w-px h-8 bg-white/20 dark:bg-zinc-800/50 self-center mx-1 shrink-0"
  }



  const [isOverflowOpen, setIsOverflowOpen] = React.useState(false)

  const [windowDimensions, setWindowDimensions] = React.useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const displayApps = React.useMemo(() => {
    if (!openApps || openApps.length === 0) return []

    const seen = new Set<string>()
    const appsList: { id: string; app: App }[] = []

    for (const tab of openApps) {
      if (tab.app && tab.app.id && !seen.has(tab.app.id)) {
        // Ignora apps de sistema que já possuem botões fixos dedicados e guias transitórias como Nova Guia
        const isNewTab =
          tab.app.slug === "new-tab" ||
          tab.app.id === "new-tab" ||
          tab.app.id.startsWith("new-tab-") ||
          tab.app.name === "Nova Guia"

        if (tab.app.id === "settings" || isNewTab) {
          continue
        }
        seen.add(tab.app.id)
        appsList.push({
          id: tab.app.id,
          app: tab.app,
        })
      }
    }

    if (dockAppIds && dockAppIds.length > 0) {
      return appsList.sort((a, b) => {
        const idxA = dockAppIds.indexOf(a.app.id)
        const idxB = dockAppIds.indexOf(b.app.id)
        if (idxA !== -1 && idxB !== -1) return idxA - idxB
        if (idxA !== -1) return -1
        if (idxB !== -1) return 1
        return 0
      })
    }

    return appsList
  }, [openApps, dockAppIds])
  const displayWidgets = widgetsConfig.filter((w) => dockWidgetIds.includes(w.id)).slice(0, 4)

  const activeTab = openApps.find((tab) => tab.instanceId === activeAppId || tab.app?.id === activeAppId)
  const activeAppDbId = activeTab?.app?.id

  const applicableWidgets = React.useMemo(() => {
    if (!widgetsConfig || widgetsConfig.length === 0) return []
    return widgetsConfig.filter((w) => {
      if (!w.enabled) return false
      if (!activeAppId || activeAppId === "desk") return true
      if (w.allAppsVisible === false && w.visibleInApps) {
        return !!activeAppDbId && w.visibleInApps.includes(activeAppDbId)
      }
      return true
    })
  }, [widgetsConfig, activeAppId, activeAppDbId])

  const { visibleApps, overflowApps } = React.useMemo(() => {
    const screenDimension = isVertical ? windowDimensions.height : windowDimensions.width
    const itemSlotSize = dockNoMargin ? dockSize + 4 : dockSize + 12

    // Itens fixos: Início (1), Widgets (1), Ajustes (1)
    const fixedItemsCount = 3
    const fixedSpace = fixedItemsCount * itemSlotSize + 48 + 40

    // O dock não deve exceder 70% da dimensão útil da tela
    const maxDockLength = screenDimension * 0.70
    const availableSpaceForApps = Math.max(0, maxDockLength - fixedSpace)

    // Quantidade máxima de apps calculada (com limite padrão de 8 para harmonia)
    const calculatedMax = Math.max(2, Math.min(8, Math.floor(availableSpaceForApps / itemSlotSize)))

    if (displayApps.length > calculatedMax) {
      const limit = Math.max(1, calculatedMax - 1)
      return {
        visibleApps: displayApps.slice(0, limit),
        overflowApps: displayApps.slice(limit),
      }
    }

    return {
      visibleApps: displayApps,
      overflowApps: [],
    }
  }, [isVertical, windowDimensions, dockNoMargin, dockSize, displayApps])

  const isAnyOverflowAppOpen = React.useMemo(() => {
    return overflowApps.some(item => openApps.some(tab => tab.app.id === item.app.id))
  }, [overflowApps, openApps])

  const isAnyOverflowAppActive = React.useMemo(() => {
    return activeAppId !== null && overflowApps.some(item => {
      const activeTab = openApps.find(tab => tab.instanceId === activeAppId)
      return activeTab?.app.id === item.app.id
    })
  }, [overflowApps, activeAppId, openApps])

  let tooltipPosClass = "bottom-full left-1/2 -translate-x-1/2 mb-2.5"
  if (dockPosition === "left") {
    tooltipPosClass = "left-full top-1/2 -translate-y-1/2 ml-2.5"
  } else if (dockPosition === "right") {
    tooltipPosClass = "right-full top-1/2 -translate-y-1/2 mr-2.5"
  } else if (dockPosition === "top") {
    tooltipPosClass = "top-full left-1/2 -translate-x-1/2 mt-2.5"
  }

  let indicatorClass = "absolute -bottom-1.5 size-1.5 rounded-full bg-white dark:bg-white animate-pulse"
  if (dockPosition === "top") {
    indicatorClass = "absolute -top-1.5 size-1.5 rounded-full bg-white dark:bg-white animate-pulse"
  } else if (dockPosition === "left") {
    indicatorClass = "absolute -left-1.5 size-1.5 rounded-full bg-white dark:bg-white animate-pulse"
  } else if (dockPosition === "right") {
    indicatorClass = "absolute -right-1.5 size-1.5 rounded-full bg-white dark:bg-white animate-pulse"
  }

  const getButtonRadius = (type: "first" | "middle" | "last" | "single") => {
    if (!dockNoMargin || type === "single") return "rounded-xl"
    if (isVertical) {
      if (type === "first") return "rounded-t-xl rounded-b-none"
      if (type === "last") return "rounded-b-xl rounded-t-none"
      return "rounded-none"
    } else {
      if (type === "first") return "rounded-l-xl rounded-r-none"
      if (type === "last") return "rounded-r-xl rounded-l-none"
      return "rounded-none"
    }
  }

  const getItemBorder = (isLastItem: boolean) => {
    if (!dockNoMargin || isLastItem) return "border-transparent"
    return isVertical
      ? "border-b border-zinc-200/20 dark:border-zinc-800/30"
      : "border-r border-zinc-200/20 dark:border-zinc-800/30"
  }

  const totalAppsCount = visibleApps.length + (overflowApps.length > 0 ? 1 : 0)
  const hasWidgets = displayWidgets.length > 0

  let triggerClass = ""
  let triggerBarClass = ""

  if (dockPosition === "bottom") {
    let alignClass = "left-1/2 -translate-x-1/2"
    if (dockAlignment === "left") alignClass = "left-10"
    else if (dockAlignment === "right") alignClass = "right-10"
    triggerClass = `fixed bottom-0 ${alignClass} w-36 h-2 rounded-t-lg bg-zinc-400/40 dark:bg-zinc-700/40 hover:h-3.5 hover:bg-[#004EB1]/80 cursor-grab active:cursor-grabbing flex items-center justify-center gap-1 group shadow-md border-t border-x border-white/10 z-45`
    triggerBarClass = "w-8 h-1 rounded-full bg-zinc-300 dark:bg-zinc-500 group-hover:bg-white transition-colors"
  } else if (dockPosition === "top") {
    let alignClass = "left-1/2 -translate-x-1/2"
    if (dockAlignment === "left") alignClass = "left-10"
    else if (dockAlignment === "right") alignClass = "right-10"
    triggerClass = `fixed top-0 ${alignClass} w-36 h-2 rounded-b-lg bg-zinc-400/40 dark:bg-zinc-700/40 hover:h-3.5 hover:bg-[#004EB1]/80 cursor-grab active:cursor-grabbing flex items-center justify-center gap-1 group shadow-md border-b border-x border-white/10 z-45`
    triggerBarClass = "w-8 h-1 rounded-full bg-zinc-300 dark:bg-zinc-500 group-hover:bg-white transition-colors"
  } else if (dockPosition === "left") {
    let alignClass = "top-1/2 -translate-y-1/2"
    if (dockAlignment === "left") alignClass = "top-10"
    else if (dockAlignment === "right") alignClass = "bottom-10"
    triggerClass = `fixed left-0 ${alignClass} w-2 h-36 rounded-r-lg bg-zinc-400/40 dark:bg-zinc-700/40 hover:w-3.5 hover:bg-[#004EB1]/80 cursor-grab active:cursor-grabbing flex flex-col items-center justify-center gap-1 group shadow-md border-r border-y border-white/10 z-45`
    triggerBarClass = "w-1 h-8 rounded-full bg-zinc-300 dark:bg-zinc-500 group-hover:bg-white transition-colors"
  } else if (dockPosition === "right") {
    let alignClass = "top-1/2 -translate-y-1/2"
    if (dockAlignment === "left") alignClass = "top-10"
    else if (dockAlignment === "right") alignClass = "bottom-10"
    triggerClass = `fixed right-0 ${alignClass} w-2 h-36 rounded-l-lg bg-zinc-400/40 dark:bg-zinc-700/40 hover:w-3.5 hover:bg-[#004EB1]/80 cursor-grab active:cursor-grabbing flex flex-col items-center justify-center gap-1 group shadow-md border-l border-y border-white/10 z-45`
    triggerBarClass = "w-1 h-8 rounded-full bg-zinc-300 dark:bg-zinc-500 group-hover:bg-white transition-colors"
  }

  return (
    <>
      {/* Backdrop transparente para capturar cliques fora do Dock sobre iframes e apps */}
      {activeAppId !== null && isDockVisible && (
        <div
          className="fixed inset-0 z-40 bg-transparent"
          onPointerDown={() => {
            userJustClickedOutsideRef.current = Date.now()
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current)
              hideTimeoutRef.current = null
            }
            setIsDockVisible(false)
            setIsOverflowOpen(false)
            setIsWidgetsOpen(false)
          }}
          onContextMenu={(e) => {
            userJustClickedOutsideRef.current = Date.now()
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current)
              hideTimeoutRef.current = null
            }
            setIsDockVisible(false)
            setIsOverflowOpen(false)
            setIsWidgetsOpen(false)
          }}
        />
      )}

      {activeAppId !== null && !isDockVisible && (
        <div
          className={triggerClass}
          onMouseEnter={handleMouseEnter}
          onMouseDown={handleDockDragStart}
          title="Arraste para mover para outra borda da tela ou passe o mouse para abrir"
        >
          <div className={triggerBarClass} />
        </div>
      )}

      <div
        ref={dockRef}
        className={`hidden md:block ${dockPosClass} z-50 transition-all duration-300 ease-out origin-center ${dockAnimationClass}`}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div
          onContextMenu={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setContextMenu({ x: e.clientX, y: e.clientY, type: "dock" })
          }}
          className={`${containerClass} bg-zinc-900/35 dark:bg-black/45 backdrop-blur-xl border border-white/10 dark:border-zinc-800/30 shadow-2xl relative`}
        >



          <div className={`relative group flex flex-col items-center ${getItemBorder(totalAppsCount === 0 && !hasWidgets)}`}>
            <button
              onClick={() => {
                setActiveAppId(null)
                setSplitActiveAppId(null)
              }}
              className={`${getButtonRadius(totalAppsCount === 0 ? "single" : "first")} overflow-hidden flex items-center justify-center shadow-md hover:scale-125 hover:-translate-y-1.5 transition-all duration-200 origin-center cursor-pointer z-10 hover:z-20`}
              style={{ width: dockSize, height: dockSize }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logo.png"
                alt="Bold Workspace"
                className="w-full h-full object-cover select-none pointer-events-none"
                draggable={false}
              />
            </button>
            {activeAppId === null && (
              <div className={indicatorClass} />
            )}
            <div className={`${tooltipPosClass} absolute px-2.5 py-1 text-[10px] font-extrabold text-white bg-zinc-900/90 dark:bg-zinc-800/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg border border-white/10 font-sans z-55`}>
              Início
            </div>
          </div>

          <div className={separatorClass} />

          {visibleApps.map(({ id, app }) => {
            const isActive = activeAppId !== null && openApps.find(tab => tab.instanceId === activeAppId)?.app.id === app.id

            return (
              <div
                key={id}
                draggable
                onContextMenu={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setContextMenu({ x: e.clientX, y: e.clientY, type: "app", app })
                }}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move"
                  e.dataTransfer.setData("dockAppId", app.id)
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = "move"
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const draggedId = e.dataTransfer.getData("dockAppId")
                  if (!draggedId || draggedId === app.id) return
                  setDockAppIds(prev => {
                    const ordered = prev.length > 0 ? [...prev] : displayApps.map(d => d.app.id)
                    const fromIdx = ordered.indexOf(draggedId)
                    const toIdx = ordered.indexOf(app.id)
                    if (fromIdx === -1 || toIdx === -1) return prev
                    const next = [...ordered]
                    next.splice(fromIdx, 1)
                    next.splice(toIdx, 0, draggedId)
                    return next
                  })
                }}
                className={`relative group flex flex-col items-center ${getItemBorder(false)} cursor-grab active:cursor-grabbing`}
              >
                <button
                  onClick={() => handleDockAppClick(app)}
                  className={`${getButtonRadius("middle")} bg-white dark:bg-zinc-900 border flex items-center justify-center shadow-md hover:scale-125 hover:-translate-y-1.5 transition-all duration-200 origin-center cursor-pointer p-1.5 shrink-0 z-10 hover:z-20 ${isActive
                    ? "border-white ring-2 ring-white/60"
                    : dockNoMargin ? "border-transparent" : "border-white/20"
                    }`}
                  style={{ width: dockSize, height: dockSize }}
                >
                  <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size={dockSize >= 56 ? "large" : dockSize >= 44 ? "medium" : "small"} />
                </button>
                {isActive && (
                  <div className={indicatorClass} />
                )}
                <div className={`${tooltipPosClass} absolute px-2.5 py-1 text-[10px] font-extrabold text-white bg-zinc-900/90 dark:bg-zinc-800/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg border border-white/10 font-sans z-55`}>
                  {app.name}
                </div>
              </div>
            )
          })}

          {overflowApps.length > 0 && (
            <Popover open={isOverflowOpen} onOpenChange={setIsOverflowOpen}>
              <div className={`relative group flex flex-col items-center ${getItemBorder(false)}`}>
                <PopoverTrigger
                  className={`${getButtonRadius("middle")} bg-white dark:bg-zinc-900 border flex items-center justify-center shadow-md hover:scale-125 hover:-translate-y-1.5 transition-all duration-200 origin-center cursor-pointer p-1.5 shrink-0 z-10 hover:z-20 ${isAnyOverflowAppActive
                    ? "border-white ring-2 ring-white/60"
                    : dockNoMargin ? "border-transparent" : "border-white/20"
                    }`}
                  style={{ width: dockSize, height: dockSize }}
                >
                  <MoreHorizontal className="text-zinc-700 dark:text-zinc-200" style={{ width: dockSize * 0.45, height: dockSize * 0.45 }} />
                </PopoverTrigger>
                {isAnyOverflowAppActive && (
                  <div className={indicatorClass} />
                )}
                <div className={`${tooltipPosClass} absolute px-2.5 py-1 text-[10px] font-extrabold text-white bg-zinc-900/90 dark:bg-zinc-800/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg border border-white/10 font-sans z-55`}>
                  Mais Aplicativos
                </div>
              </div>

              <PopoverContent
                ref={popoverContentRef}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                side={
                  dockPosition === "bottom" ? "top" :
                    dockPosition === "top" ? "bottom" :
                      dockPosition === "left" ? "right" : "left"
                }
                align="center"
                sideOffset={12}
                className="w-72 p-2.5 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-zinc-200/90 dark:border-zinc-800/90 rounded-2xl shadow-2xl font-sans z-[99999] animate-in fade-in zoom-in-95 duration-150"
              >
                <div className="max-h-60 overflow-y-auto space-y-1 pr-0.5">
                  {overflowApps.map(({ id, app }) => {
                    const isActive = activeAppId !== null && openApps.find(tab => tab.instanceId === activeAppId)?.app.id === app.id

                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          handleDockAppClick(app)
                          setIsOverflowOpen(false)
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setContextMenu({ x: e.clientX, y: e.clientY, type: "app", app })
                          setIsOverflowOpen(false)
                        }}
                        className={`w-full flex items-center justify-between p-2 rounded-xl text-left transition-all cursor-pointer group ${isActive
                          ? "bg-[#004EB1]/10 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 font-bold border border-blue-500/20"
                          : "hover:bg-zinc-100 dark:hover:bg-zinc-800/70 text-zinc-700 dark:text-zinc-300"
                          }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="size-7 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0 shadow-xs border border-zinc-200/50 dark:border-zinc-700/50">
                            <AppIcon iconUrl={app.iconUrl} iframeUrl={app.iframeUrl} name={app.name} size="small" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold truncate leading-tight group-hover:text-zinc-900 dark:group-hover:text-white">
                              {app.name}
                            </p>
                          </div>
                        </div>

                        {isActive && (
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            <div className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400">
                              Ativo
                            </span>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}



          {/* Widgets Flutuantes (Quebra-Cabeça) */}
          <Popover open={isWidgetsOpen} onOpenChange={setIsWidgetsOpen}>
            <div className={`relative group flex flex-col items-center ${getItemBorder(false)}`}>
              <PopoverTrigger
                className={`${getButtonRadius("middle")} bg-gradient-to-tr from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 flex items-center justify-center shadow-md hover:scale-125 hover:-translate-y-1.5 transition-all duration-200 origin-center cursor-pointer border border-white/20 z-10 hover:z-20 ${isWidgetsOpen || activeFloatingWidgets.length > 0 ? "ring-2 ring-white/60" : ""
                  }`}
                style={{ width: dockSize, height: dockSize }}
              >
                <Puzzle className="text-white" style={{ width: dockSize * 0.5, height: dockSize * 0.5 }} />
                {activeFloatingWidgets.length > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#004EB1] px-1 text-[9px] font-extrabold text-white shadow-sm ring-2 ring-white dark:ring-zinc-900 z-30">
                    {activeFloatingWidgets.length}
                  </span>
                )}
              </PopoverTrigger>
              {activeFloatingWidgets.length > 0 && (
                <div className={indicatorClass} />
              )}
              <div className={`${tooltipPosClass} absolute px-2.5 py-1 text-[10px] font-extrabold text-white bg-zinc-900/90 dark:bg-zinc-800/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg border border-white/10 font-sans z-55`}>
                {lang === "pt" ? "Widgets" : lang === "es" ? "Widgets" : "Widgets"}
              </div>
            </div>

            <PopoverContent
              ref={widgetsPopoverRef}
              onMouseEnter={handleMouseEnter}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              side={
                dockPosition === "bottom" ? "top" :
                  dockPosition === "top" ? "bottom" :
                    dockPosition === "left" ? "right" : "left"
              }
              align="center"
              sideOffset={12}
              className="w-72 p-2.5 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-zinc-200/90 dark:border-zinc-800/90 rounded-2xl shadow-2xl font-sans z-[99999] animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-zinc-100 dark:border-zinc-800">
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Puzzle className="size-3 text-indigo-500" />
                  Widgets
                  {activeFloatingWidgets.length > 0 && ` (${activeFloatingWidgets.length} ${lang === "pt" ? "ativos" : "active"})`}
                </span>
              </div>

              {applicableWidgets.length === 0 ? (
                <div className="px-2 py-4 text-xs text-zinc-450 dark:text-zinc-500 italic text-center">
                  {lang === "pt" ? "Nenhum widget disponível para este App." : lang === "es" ? "Ningún widget disponible para esta App." : "No widgets available for this App."}
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1 pr-0.5">
                  {applicableWidgets.map(widget => {
                    const isFloating = activeFloatingWidgets.includes(widget.id)
                    return (
                      <button
                        key={widget.id}
                        type="button"
                        onClick={() => handleToggleFloatingWidget(widget.id)}
                        className={`w-full flex items-center justify-between p-2 rounded-xl text-left transition-all cursor-pointer group ${isFloating
                            ? "bg-[#004EB1]/10 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 font-bold border border-blue-500/20"
                            : "hover:bg-zinc-100 dark:hover:bg-zinc-800/70 text-zinc-700 dark:text-zinc-300"
                          }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`size-7 rounded-lg flex items-center justify-center shrink-0 shadow-xs border ${isFloating
                              ? "bg-[#004EB1]/15 dark:bg-blue-900/40 border-blue-400/30 text-[#004EB1] dark:text-blue-400"
                              : "bg-zinc-100 dark:bg-zinc-800 border-zinc-200/50 dark:border-zinc-700/50 text-zinc-600 dark:text-zinc-400"
                            }`}>
                            <WidgetIcon id={widget.id} icon={widget.icon} name={widget.name} className="size-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold truncate leading-tight group-hover:text-zinc-900 dark:group-hover:text-white">
                              {widget.name}
                            </p>
                            {widget.description && (
                              <p className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate leading-tight font-normal">
                                {widget.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          <div className={`size-2 rounded-full ${isFloating ? "bg-emerald-500 animate-pulse" : "bg-zinc-300 dark:bg-zinc-700"}`} />
                          <span className={`text-[9px] font-bold ${isFloating ? "text-emerald-600 dark:text-emerald-400" : "text-zinc-400"}`}>
                            {isFloating ? (lang === "pt" ? "Ativo" : "Active") : (lang === "pt" ? "Inativo" : "Inactive")}
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Ajustes do Sistema */}
          {hasCompany && (
            <div className="relative group flex flex-col items-center">
              <button
                onClick={() => {
                  handleOpenSettingsTab()
                  setSettingsTab("company")
                }}
                className={`${getButtonRadius("last")} bg-gradient-to-tr from-zinc-500 to-zinc-400 dark:from-zinc-800 dark:to-zinc-700 flex items-center justify-center shadow-md hover:scale-125 hover:-translate-y-1.5 transition-all duration-200 origin-center cursor-pointer border border-white/20 z-10 hover:z-20 ${activeAppId === "settings" ? "ring-2 ring-white/60" : ""
                  }`}
                style={{ width: dockSize, height: dockSize }}
              >
                <Settings className="text-white" style={{ width: dockSize * 0.5, height: dockSize * 0.5 }} />
              </button>
              {activeAppId === "settings" && (
                <div className={indicatorClass} />
              )}
              <div className={`${tooltipPosClass} absolute px-2.5 py-1 text-[10px] font-extrabold text-white bg-zinc-900/90 dark:bg-zinc-800/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg border border-white/10 font-sans z-55`}>
                Ajustes do Sistema
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Menu de Contexto (Clique com Botão Direito) */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-55" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }} />
          <div
            className="fixed z-60 w-48 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl py-1.5 text-xs font-sans animate-in fade-in duration-150"
            style={{
              top: dockPosition === "bottom" ? undefined : contextMenu.y,
              bottom: dockPosition === "bottom" ? window.innerHeight - contextMenu.y : undefined,
              left: Math.min(contextMenu.x, window.innerWidth - 200),
            }}
          >
            {(contextMenu.type === "app" || contextMenu.type === "dock") && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    handleOpenSettingsTab()
                    setSettingsTab("dock")
                    setContextMenu(null)
                  }}
                  className="w-full px-3.5 py-2 text-left text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 font-semibold flex items-center gap-2 cursor-pointer border-t border-zinc-100 dark:border-zinc-800/80"
                >
                  <Sliders className="size-3.5 text-zinc-500" />
                  <span>Configurações da Dock</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
